/**
 * Text filter for the authoring Audits panel: matches an audit by caption or slug (e.g. ITEM_MATRIX_AUDIT), or a
 * check by caption or id (e.g. UNCONFIRMED). An audit whose name matches keeps all its checks; otherwise only the
 * matching checks are listed.
 */
const norm = (term: string) => (term ?? '').trim().toLowerCase();

const captionText = (caption: any): string =>
  caption == null ? '' : typeof caption === 'string' ? caption : [caption.en, caption.fr].filter(Boolean).join(' ');

export const auditNameMatches = (audit: any, term: string): boolean => {
  const t = norm(term);
  if (!t) { return true; }
  return `${audit?.slug ?? ''} ${captionText(audit?.caption)}`.toLowerCase().includes(t);
};

export const checkMatches = (check: any, term: string): boolean => {
  const t = norm(term);
  if (!t) { return true; }
  return `${check?.id ?? check?.key ?? ''} ${captionText(check?.caption)}`.toLowerCase().includes(t);
};

/** run results are stored keyed or as a list, next to bookkeeping like `__metaData` */
export const listCheckResults = (results: any): any[] =>
  Object.entries(results ?? {})
    .filter(([key, value]) => !key.startsWith('__') && value && typeof value === 'object')
    .map(([, value]) => value);

/** a result shows when the audit name matches, or the result or its configured check matches */
export const isCheckShownByText = (audit: any, result: any, term: string): boolean => {
  if (!norm(term) || auditNameMatches(audit, term) || checkMatches(result, term)) { return true; }
  const id = result?.id ?? result?.key;
  const configured = (audit?.checks ?? []).find((c: any) => c?.id === id);
  return !!configured && checkMatches(configured, term);
};

export const isAuditShownByText = (audit: any, results: any, term: string): boolean =>
  !norm(term)
  || auditNameMatches(audit, term)
  || (audit?.checks ?? []).some((c: any) => checkMatches(c, term))
  || listCheckResults(results).some(r => checkMatches(r, term));
